'use client'

import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { DemoFrame } from './demoKit'
import { useOnScreen, useSequence } from './demoHooks'
import { ResultStamp } from './demoParts'

// Cost-effective GHL team: the page's "the cheapest option is usually the
// most expensive". The cheapest freelancer's build week by week, against
// GHL Prime's, with a bar of what you actually end up paying: the original
// fee, then the rework (the page's 30–40%), then revenue lost while broken.

const PATHS = {
  cheap: {
    steps: [
      { time: 'Week 1', text: 'Paid the lowest quote' },
      { time: 'Week 3', text: 'A2P registration rejected', bad: true },
      { time: 'Week 5', text: 'Workflows break under real traffic', bad: true },
      { time: 'Week 6', text: 'Freelancer stops replying', bad: true },
      { time: 'Week 8', text: 'Second team hired to redo it', bad: true },
      { time: 'Week 12', text: 'Finally live, paid twice', bad: true },
    ],
    // Bar segments: [label, share of the bar, step it appears at]
    bar: [['Original fee', 38, 1], ['Rework: 30–40% more', 22, 5], ['Revenue lost while broken', 40, 3]],
    result: { tone: 'bad', text: 'Paid twice' },
  },
  prime: {
    steps: [
      { time: 'Week 1', text: 'Scoped with a clear price, no hidden fees' },
      { time: 'Week 2', text: 'A2P approved on the first submission' },
      { time: 'Week 3', text: 'Automations tested on edge cases' },
      { time: 'Week 4', text: 'Live and documented' },
      { time: 'Month 6', text: 'Still running, nothing to redo' },
    ],
    bar: [['One fee, no rework', 46, 1]],
    result: { tone: 'good', text: 'Paid once' },
  },
}
const DELAYS = [500, 1000, 1000, 1000, 1000, 1000, 700]

export default function TrueCost() {
  const [ref, onScreen] = useOnScreen()
  const [path, setPath] = useState('cheap')
  const [run, setRun] = useState(0)
  const [auto, setAuto] = useState(true)
  const data = PATHS[path]
  const step = useSequence(DELAYS, { run: onScreen, resetKey: `${path}-${run}` })
  const finished = step >= DELAYS.length

  useEffect(() => {
    if (!auto || path !== 'cheap' || !finished) return undefined
    const timer = setTimeout(() => setPath('prime'), 1800)
    return () => clearTimeout(timer)
  }, [auto, path, finished])

  const pick = (key) => { setAuto(false); setPath(key); setRun((n) => n + 1) }

  return (
    <DemoFrame
      frameRef={ref}
      className="svd-cost"
      title="Cheap vs cost-effective"
      label="Demo: the real cost of the cheapest GoHighLevel freelancer versus GHL Prime"
      tabs={[{ key: 'cheap', label: 'Cheapest freelancer', tone: 'bad' }, { key: 'prime', label: 'GHL Prime', tone: 'good' }]}
      activeTab={path}
      onTab={pick}
      onReplay={() => pick(path)}
    >
      <ol className="svd-journey">
        <AnimatePresence initial={false}>
          {data.steps.slice(0, Math.min(step, data.steps.length)).map((s, i) => (
            <motion.li key={`${path}-${run}-${i}`} className={s.bad ? 'is-bad' : 'is-good'} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}>
              <time>{s.time}</time>
              <span>{s.text}</span>
            </motion.li>
          ))}
        </AnimatePresence>
      </ol>
      <div className="svd-costbar">
        <span className="svd-trail-title">What you actually pay</span>
        <div className="svd-costbar-track">
          {data.bar.map(([label, share, at], i) => (
            <motion.span
              key={`${path}-${run}-${label}`}
              className={`seg-${path}-${i}`}
              initial={{ width: 0, opacity: 0 }}
              animate={step >= at ? { width: `${share}%`, opacity: 1 } : { width: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
              title={label}
            >
              <em>{label}</em>
            </motion.span>
          ))}
        </div>
      </div>
      <ResultStamp show={finished} tone={data.result.tone} text={data.result.text} id={`${path}-${run}`} />
    </DemoFrame>
  )
}
